import { api } from './utils';

// Check if notifications are supported
export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    return { granted: false, reason: 'Not supported' };
  }
  
  const permission = await Notification.requestPermission();
  return { granted: permission === 'granted', permission };
};

// Get current permission status
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

// Subscribe to push notifications
export const subscribeToPush = async () => {
  if (!isNotificationSupported()) {
    throw new Error('Notifications not supported');
  }
  
  const permission = await requestNotificationPermission();
  if (!permission.granted) {
    throw new Error('Permission denied');
  }
  
  const registration = await navigator.serviceWorker.ready;
  
  // For demo purposes, we'll use a mock subscription
  // In production, you'd use VAPID keys
  const subscription = {
    endpoint: `https://push.example.com/${Date.now()}`,
    keys: {
      p256dh: 'mock_p256dh_key',
      auth: 'mock_auth_key'
    }
  };
  
  // Register with backend
  await api.post('/notifications/subscribe', subscription);
  
  return subscription;
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async (endpoint) => {
  await api.delete('/notifications/unsubscribe', {
    params: { endpoint }
  });
};

// Show local notification
export const showNotification = (title, options = {}) => {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return false;
  }
  
  const notification = new Notification(title, {
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [200, 100, 200],
    ...options
  });
  
  notification.onclick = () => {
    window.focus();
    notification.close();
    if (options.onClick) options.onClick();
  };
  
  return true;
};

// Get receivable reminders
export const getReceivableReminders = async () => {
  try {
    const response = await api.get('/notifications/receivable-reminders');
    return response.data;
  } catch (error) {
    console.error('Failed to get reminders:', error);
    return [];
  }
};

// Check and show receivable reminders
export const checkReceivableReminders = async () => {
  const reminders = await getReceivableReminders();
  
  // Show notifications for urgent items
  const urgentReminders = reminders.filter(r => r.urgency === 'urgent');
  
  urgentReminders.forEach(reminder => {
    showNotification(`Payment Due: ${reminder.customer_name}`, {
      body: `₹${(reminder.balance_pending / 100000).toFixed(2)}L pending - Due ${reminder.delivery_date}`,
      tag: `receivable-${reminder.receivable_id}`,
      requireInteraction: true,
      actions: [
        { action: 'view', title: 'View' },
        { action: 'dismiss', title: 'Dismiss' }
      ]
    });
  });
  
  return reminders;
};

// Schedule daily reminder check
export const scheduleDailyReminders = () => {
  // Check immediately
  checkReceivableReminders();
  
  // Check every hour
  const intervalId = setInterval(() => {
    const now = new Date();
    // Only check between 8 AM and 8 PM
    if (now.getHours() >= 8 && now.getHours() <= 20) {
      checkReceivableReminders();
    }
  }, 60 * 60 * 1000); // Every hour
  
  return () => clearInterval(intervalId);
};

// Format reminder for display
export const formatReminder = (reminder) => {
  const urgencyColors = {
    urgent: 'bg-red-100 text-red-700 border-red-200',
    upcoming: 'bg-amber-100 text-amber-700 border-amber-200',
    scheduled: 'bg-zinc-100 text-zinc-700 border-zinc-200'
  };
  
  const urgencyLabels = {
    urgent: 'Due Today/Tomorrow',
    upcoming: 'Due This Week',
    scheduled: 'Upcoming'
  };
  
  return {
    ...reminder,
    colorClass: urgencyColors[reminder.urgency] || urgencyColors.scheduled,
    label: urgencyLabels[reminder.urgency] || 'Scheduled'
  };
};
